import React, { useContext } from 'react';
import { useAppSelector } from 'hooks';
import { ApplicationFormContext } from 'contexts/ApplicationFormContext';
import useFormBinderDisabled from 'hooks/useFormBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
// @ts-ignore
import { HashLink } from 'react-router-hash-link';

import {
  Box,
  Grid,
  GridItem,
  Icon,
  Text,
  VStack,
  HStack,
  Badge,
} from '@chakra-ui/react';

import { CheckCircleIcon } from '@chakra-ui/icons';

import capitalizeFirstLetterInAllWords from 'helpers/capitalizeFirstLetterInAllWords';

import isDeferralAllowed from 'helpers/isDeferralAllowed';
import type { APIRemarkFile } from 'types/api.types';

import { FILE_TYPE, WIP_STATUS_APPEAL_APPROVED } from 'variables/constants';
interface SharedMissingFieldsProps {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm';
  mandatoryFileValidations?: string[];
}

const SharedMissingFields: React.FC<SharedMissingFieldsProps> = ({
  formStateKey,
  mandatoryFileValidations,
}) => {
  const { validations } = useContext(ApplicationFormContext) as {
    validations: Validation[];
  };

  const isDisabled = useFormBinderDisabled(formStateKey);
  const isDlhRequired = useFeatureFlag('enable-dlh-required');
  const isOwnershipOnlyDeferralEnabled = useFeatureFlag(
    'enable-ownership-only-deferral',
  );
  const isBindRequired = (f: APIRemarkFile) =>
    !isDeferralAllowed(
      f.validation_key,
      validations,
      isOwnershipOnlyDeferralEnabled,
    );

  const appeal = useAppSelector((state) => state[formStateKey].appeal);

  const missingFields = useAppSelector(
    (state) => state[formStateKey].missingMandatoryFields,
  );

  const missingRemarks = useAppSelector((state) => {
    if (appeal?.status === WIP_STATUS_APPEAL_APPROVED) {
      return [];
    }

    const allRemarks = state[formStateKey].remarks.filter(
      (r) =>
        r.status !== 'approved' &&
        !(r.validation_key === '133b' && r.status === 'failed'),
    );

    return allRemarks;
  });

  const missingFiles = useAppSelector((state) => {
    if (appeal?.status === WIP_STATUS_APPEAL_APPROVED) {
      return [];
    }

    // Add mandatory field to all files and filter to only non-approved and non-deferred files
    const allFiles = state[formStateKey].files
      .map((f) => ({
        ...f,
        mandatory:
          mandatoryFileValidations.includes(f.validation_key) ||
          (isDlhRequired &&
            f.validation_key === 'driver_training_certificate' &&
            f.mandatory === true) ||
          (f.validation_key === '133b' && f.mandatory === true) ||
          (isOwnershipOnlyDeferralEnabled && isBindRequired(f)),
      }))
      .filter((f) => f.status !== 'approved' && !f.is_deferred);

    // Filter to only mandatory files
    const mandatoryFiles = allFiles.filter((f) => f.mandatory);

    return mandatoryFiles;
  });
  const isMandatoryFieldsFilled = missingFields.length === 0;
  const isRemarksFilled = missingRemarks.length === 0;
  const isUploadsFilled = missingFiles.length === 0;

  const scrollWithOffset = (el: any, yOffset: number) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
  };

  const getValidationName = (id: string) => {
    const validation = validations.find((v) => v.validation_id === id);

    if (validation) return validation.validation_name;

    const name = id.replace(/_/g, ' ');

    return capitalizeFirstLetterInAllWords(name);
  };

  const getFileName = (fileType: number) => {
    return (
      Object.values(FILE_TYPE).find((type: any) => type.type === fileType)
        ?.text || 'Unknown File'
    );
  };

  const getSubFieldName = (name: string) => {
    if (name === 'global') return '';
    const newName = capitalizeFirstLetterInAllWords(name.replace(/-/g, ' '));
    return ` (${newName})`;
  };

  const getToURL = (validationId: string, key: string) => {
    if (key === 'global') {
      return `#${validationId}`;
    }

    return `#${validationId}-${key}`;
  };

  if (isDisabled) {
    return null;
  }

  return (
    <VStack spacing={6} align="stretch" mb="20px">
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }} gap={6}>
        <GridItem>
          <Box
            bg="white"
            border="2px"
            borderColor={isMandatoryFieldsFilled ? 'green.200' : 'yellow.200'}
            borderRadius="lg"
            overflow="hidden"
            h="fit-content"
            _hover={{ shadow: 'md' }}
            transition="all 0.2s"
          >
            <Box
              bg={isMandatoryFieldsFilled ? 'green.50' : 'yellow.50'}
              px={4}
              py={3}
              borderBottom="1px"
              borderColor={isMandatoryFieldsFilled ? 'green.200' : 'yellow.200'}
            >
              <HStack spacing={2}>
                <Icon
                  as={CheckCircleIcon}
                  color={isMandatoryFieldsFilled ? 'green.500' : 'yellow.500'}
                  w={5}
                  h={5}
                />
                <Text
                  fontWeight="semibold"
                  color={isMandatoryFieldsFilled ? 'green.700' : 'yellow.700'}
                >
                  Mandatory Fields
                </Text>
                <Badge
                  colorScheme={isMandatoryFieldsFilled ? 'green' : 'yellow'}
                  variant="solid"
                  borderRadius="full"
                >
                  {isMandatoryFieldsFilled
                    ? 'Complete'
                    : `${missingFields.length} missing`}
                </Badge>
              </HStack>
            </Box>
            <Box p={4}>
              {isMandatoryFieldsFilled ? (
                <Text color="green.600" textAlign="center" py={2}>
                  All mandatory fields are completed!
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {missingFields.map((field) => (
                    <HashLink
                      key={field.name}
                      smooth
                      to={`#${field['name']}`}
                      scroll={(el: any) => scrollWithOffset(el, -184)}
                    >
                      <Box
                        p={2}
                        borderRadius="md"
                        _hover={{
                          bg: 'yellow.50',
                          borderColor: 'yellow.300',
                        }}
                        border="1px"
                        borderColor="gray.200"
                        transition="all 0.2s"
                        cursor="pointer"
                      >
                        <Text fontSize="sm" color="gray.700">
                          {field['name']}
                        </Text>
                      </Box>
                    </HashLink>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>
        </GridItem>

        <GridItem>
          <Box
            bg="white"
            border="2px"
            borderColor={isRemarksFilled ? 'green.200' : 'yellow.200'}
            borderRadius="lg"
            overflow="hidden"
            h="fit-content"
            _hover={{ shadow: 'md' }}
            transition="all 0.2s"
          >
            <Box
              bg={isRemarksFilled ? 'green.50' : 'yellow.50'}
              px={4}
              py={3}
              borderBottom="1px"
              borderColor={isRemarksFilled ? 'green.200' : 'yellow.200'}
            >
              <HStack spacing={2}>
                <Icon
                  as={CheckCircleIcon}
                  color={isRemarksFilled ? 'green.500' : 'yellow.500'}
                  w={5}
                  h={5}
                />
                <Text
                  fontWeight="semibold"
                  color={isRemarksFilled ? 'green.700' : 'yellow.700'}
                >
                  Remarks
                </Text>
                <Badge
                  colorScheme={isRemarksFilled ? 'green' : 'yellow'}
                  variant="solid"
                  borderRadius="full"
                >
                  {isRemarksFilled
                    ? 'Complete'
                    : `${missingRemarks.length} missing`}
                </Badge>
              </HStack>
            </Box>
            <Box p={4}>
              {isRemarksFilled ? (
                <Text color="green.600" textAlign="center" py={2}>
                  All remarks are completed!
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {missingRemarks.map((remark) => (
                    <HashLink
                      key={`${remark.validation_key}-${remark.item_key}`}
                      smooth
                      to={getToURL(remark.validation_key, remark.item_key)}
                      scroll={(el: any) => scrollWithOffset(el, -200)}
                    >
                      <Box
                        p={2}
                        borderRadius="md"
                        _hover={{
                          bg: 'yellow.50',
                          borderColor: 'yellow.300',
                        }}
                        border="1px"
                        borderColor="gray.200"
                        transition="all 0.2s"
                        cursor="pointer"
                      >
                        <Text fontSize="sm" color="gray.700">
                          {getValidationName(remark.validation_key)}
                          {getSubFieldName(remark.item_key)}
                        </Text>
                      </Box>
                    </HashLink>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>
        </GridItem>

        <GridItem>
          <Box
            bg="white"
            border="2px"
            borderColor={isUploadsFilled ? 'green.200' : 'yellow.200'}
            borderRadius="lg"
            overflow="hidden"
            h="fit-content"
            _hover={{ shadow: 'md' }}
            transition="all 0.2s"
          >
            <Box
              bg={isUploadsFilled ? 'green.50' : 'yellow.50'}
              px={4}
              py={3}
              borderBottom="1px"
              borderColor={isUploadsFilled ? 'green.200' : 'yellow.200'}
            >
              <HStack spacing={2}>
                <Icon
                  as={CheckCircleIcon}
                  color={isUploadsFilled ? 'green.500' : 'yellow.500'}
                  w={5}
                  h={5}
                />
                <Text
                  fontWeight="semibold"
                  color={isUploadsFilled ? 'green.700' : 'yellow.700'}
                >
                  Uploads
                </Text>
                <Badge
                  colorScheme={isUploadsFilled ? 'green' : 'yellow'}
                  variant="solid"
                  borderRadius="full"
                >
                  {isUploadsFilled
                    ? 'Complete'
                    : `${missingFiles.length} missing`}
                </Badge>
              </HStack>
            </Box>
            <Box p={4}>
              {isUploadsFilled ? (
                <Text color="green.600" textAlign="center" py={2}>
                  All uploads are completed!
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {missingFiles.map((file) => (
                    <HashLink
                      key={`${file.validation_key}-${file.item_key}`}
                      smooth
                      to={getToURL(file.validation_key, file.item_key)}
                      scroll={(el: any) => scrollWithOffset(el, -200)}
                    >
                      <Box
                        p={2}
                        borderRadius="md"
                        _hover={{
                          bg: 'yellow.50',
                          borderColor: 'yellow.300',
                        }}
                        border="1px"
                        borderColor="gray.200"
                        transition="all 0.2s"
                        cursor="pointer"
                      >
                        <Text fontSize="sm" color="gray.700">
                          {getFileName(file.type)}
                          {getSubFieldName(file.item_key)}
                        </Text>
                      </Box>
                    </HashLink>
                  ))}
                </VStack>
              )}
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </VStack>
  );
};

export default SharedMissingFields;
